import React, { createContext, useContext, useEffect } from "react";
import { getLatestReport, getReport, Report } from "@/queries/report";
import { useToast } from "@/components/ui/use-toast";
import { useLocation } from "react-router-dom";

// The context
type ReportContextType = {
  reportId: number;
  report: Report | null;
  forced: boolean;
  formattedDate: string;
  hasReport: boolean;
  isLoading: boolean;
};

// Create the context
const ReportContext = createContext<ReportContextType | undefined>(undefined);

// Function to use the context
export function useReportContext() {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error("useReportContext must be used within a ReportProvider");
  }
  return context;
}

// Define the provider
export function ReportProvider({ children }: React.PropsWithChildren<{}>) {
  const location = useLocation();
  const { toast } = useToast();

  const searchParams = new URLSearchParams(location.search);
  const forceReportId = searchParams.get("report_id");

  // Validate forceReportId is a valid number
  const parsedReportId = forceReportId ? parseInt(forceReportId, 10) : null;
  const isValidReportId = Boolean(
    parsedReportId && !isNaN(parsedReportId) && parsedReportId > 0
  );

  // Try to fetch forced report first
  const forcedReportQuery = getReport({
    report: parsedReportId || undefined,
    enabled: isValidReportId,
  });

  // Fallback to latest report if forced report fails (404) or not specified
  const latestReportQuery = getLatestReport();

  // Use forced report if available and no error, otherwise use latest
  const report =
    isValidReportId && forcedReportQuery.data && !forcedReportQuery.error
      ? forcedReportQuery.data
      : latestReportQuery.data;

  const isLoading =
    (isValidReportId && forcedReportQuery.isLoading) ||
    latestReportQuery.isLoading;

  // Show toast if forced report returned 404 (still building)
  useEffect(() => {
    if (
      isValidReportId &&
      forcedReportQuery.error &&
      !forcedReportQuery.isLoading
    ) {
      toast({
        title: "Report Still Building",
        description: `Report #${parsedReportId} is not ready yet. Showing the latest available forecast instead.`,
        variant: "default",
        duration: 5000,
      });
    }
  }, [
    forcedReportQuery.error,
    forcedReportQuery.isLoading,
    isValidReportId,
    toast,
    parsedReportId,
  ]);

  const activeReportId = report?.id ?? parsedReportId ?? 0;
  const hasReport = Boolean(report);
  let formattedDate = "No report available";

  // Ensure we have a valid date source
  if (report) {
    if (!report.target_date && !report.created_at) {
      console.error("Report missing both target_date and created_at");
    } else {
      const date = new Date(
        report.target_date
          ? report.target_date + " 00:00:00"
          : (report.created_at || "").replace("Z", "")
      );

      if (isNaN(date.getTime())) {
        console.error("Invalid date:", report.target_date || report.created_at);
      } else {
        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "short",
          day: "2-digit",
        };
        formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);
      }
    }
  }

  return (
    <ReportContext.Provider
      value={{
        reportId: activeReportId,
        report,
        forced: isValidReportId,
        formattedDate,
        hasReport,
        isLoading,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
}
